'use client';

import { useTranslations } from 'next-intl';
import FadeInSection from '@/components/ui/FadeInSection';

/* Small check used in every card's task list. Mirrors the marks used in
   AboutSection / ServiceSection so the iconography stays consistent. */
function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="mt-0.5 shrink-0 text-primary"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Turnkey finishing — a home. */
function HomeIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 21v-6h5v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* General renovation — crossed tools. */
function ToolsIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M14.7 6.3a3.5 3.5 0 0 0 4.6 4.6l-2-2 1.4-1.4 2 2a5.5 5.5 0 0 1-7.4-7.4l2 2-1.4 1.4-2-2a3.5 3.5 0 0 0 .2 2.2Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m6 8 5.5 5.5M4 14l6 6M14 14l6 6M9 19l-3 3-2-2 3-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Specialized services — drafting compass. */
function CompassIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="12" cy="5" r="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m10.6 6.7-5.6 13M13.4 6.7l5.6 13M8.5 16h7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function DiscoverSection() {
  const t = useTranslations('discover');

  const cards = [
    {
      key: 'card1',
      Icon: HomeIcon,
      eyebrow: t('card1Eyebrow'),
      title: t('card1Title'),
      desc: t('card1Desc'),
      items: [t('card1Item1'), t('card1Item2'), t('card1Item3'), t('card1Item4'), t('card1Item5')],
    },
    {
      key: 'card2',
      Icon: ToolsIcon,
      eyebrow: t('card2Eyebrow'),
      title: t('card2Title'),
      desc: t('card2Desc'),
      items: [t('card2Item1'), t('card2Item2'), t('card2Item3'), t('card2Item4'), t('card2Item5')],
    },
    {
      key: 'card3',
      Icon: CompassIcon,
      eyebrow: t('card3Eyebrow'),
      title: t('card3Title'),
      desc: t('card3Desc'),
      items: [t('card3Item1'), t('card3Item2'), t('card3Item3'), t('card3Item4'), t('card3Item5')],
    },
  ];

  return (
    <section
      id="uslugi"
      className="scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop"
    >
      <div className="mx-auto w-full max-w-6xl">
        {/* Centered header — eyebrow, headline, supporting line */}
        <FadeInSection className="mx-auto max-w-2xl text-center">
          <p className="text-label-caps uppercase tracking-widest text-primary">{t('eyebrow')}</p>
          <h2 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
            {t('headline')}
          </h2>
          <p className="mt-stack-md text-body-md text-on-surface-variant md:text-body-lg">
            {t('subheadline')}
          </p>
        </FadeInSection>

        {/* Three project-type cards */}
        <ul className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {cards.map(({ key, Icon, eyebrow, title, desc, items }, i) => (
            <li key={key} className="forest-card">
              <FadeInSection delay={i * 0.12} className="h-full">
                <div className="flex h-full flex-col px-6 pb-6 pt-5 lg:px-8 lg:pb-8 lg:pt-6">
                  <span className="inline-flex text-primary">
                    <Icon />
                  </span>

                  <p className="mt-stack-sm text-label-caps uppercase tracking-widest text-outline">
                    {eyebrow}
                  </p>
                  <h3 className="mt-stack-sm font-headline text-headline-md font-bold text-on-surface">
                    {title}
                  </h3>
                  <p className="mt-stack-sm text-body-md text-on-surface-variant">{desc}</p>

                  <hr className="my-stack-lg border-surface-bright/15" />

                  <ul className="space-y-stack-md">
                    {items.map((item) => (
                      <li key={item} className="flex gap-stack-md">
                        <CheckIcon />
                        <span className="text-body-md text-on-surface">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInSection>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
